<template>
  <v-menu
    v-model="menuOpen"
    location="bottom"
    transition="slide-y-transition"
    :close-on-content-click="false"
    @update:model-value="onMenuToggle"
  >
    <template #activator="{ props }">
      <v-btn
        icon
        class="elevation-0"
        v-bind="props"
        :loading="$langLoading"
        aria-label="Select language"
      >
        <!-- same geometry as the user button next to it: 24px glyph in a 40px avatar -->
        <v-avatar size="40">
          <svg
            :style="`width: 24px;height: 24px;vertical-align: middle;overflow: hidden; fill: ${isThemeDark ? 'rgb(var(--v-theme-accent))' : 'rgb(var(--v-theme-primary))' }`"
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          ><path d="M547.797333 638.208l-104.405333-103.168 1.237333-1.28a720.170667 720.170667 0 0 0 152.490667-268.373333h120.448V183.082667h-287.744V100.906667H347.605333v82.218666H59.818667V265.386667h459.178666a648.234667 648.234667 0 0 1-130.304 219.946666 643.242667 643.242667 0 0 1-94.976-137.728H211.541333a722.048 722.048 0 0 0 122.453334 187.434667l-209.194667 206.378667 58.368 58.368 205.525333-205.525334 127.872 127.829334 31.232-83.84m231.424-208.426667h-82.218666l-184.96 493.312h82.218666l46.037334-123.306667h195.242666l46.464 123.306667h82.218667l-185.002667-493.312m-107.690666 287.744l66.56-178.005333 66.602666 178.005333z" fill="" /></svg>
        </v-avatar>
      </v-btn>
    </template>

    <v-card
      min-width="280"
      max-width="320"
    >
      <!-- Search input -->
      <v-card-text style="padding: 24px;">
        <v-text-field
          ref="searchInput"
          v-model="searchQuery"
          :placeholder="$t('header.searchLanguage')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          autofocus
          aria-label="Search languages"
          @keydown.enter="onEnterKey"
          @keydown.escape="onEscapeKey"
        />
      </v-card-text>

      <!-- Search hint when 1 char entered -->
      <v-card-text
        v-if="searchHint"
        class="py-1 ms-2 text-caption text-medium-emphasis"
      >
        {{ searchHint }}
      </v-card-text>

      <!-- Match count when filtering -->
      <v-card-text
        v-if="isSearchActive && matchCount > 1"
        class="py-1 ms-2 text-caption text-medium-emphasis"
      >
        {{ matchCount }} {{ $t('header.matches') }}
      </v-card-text>


      <!-- Languages list -->
      <v-list
        density="compact"
        max-height="200"
        class="overflow-y-auto"
      >
        <template v-if="filteredLanguages.length > 0">
          <v-list-item
            v-for="item in filteredLanguages"
            :key="item.locale"
            :disabled="$langLoading"
            @click="selectLanguage(item.locale)"
          >
            <template #prepend>
              <v-icon>
                mdi-earth
              </v-icon>
            </template>
            <v-list-item-title>
              <span v-html="highlightMatch(item.native, item.nativeMatch)" />
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="item.englishMatch && item.english !== item.native"
              class="text-caption"
            >
              <span v-html="highlightMatch(item.english, item.englishMatch)" />
            </v-list-item-subtitle>
            <template #append>
              <v-icon
                v-if="$current_lang === item.locale"
                color="primary"
                size="small"
              >
                mdi-check
              </v-icon>
            </template>
          </v-list-item>
        </template>

        <!-- Empty state -->
        <v-list-item v-else>
          <v-list-item-title class="text-medium-emphasis">
            {{ $t('header.noLanguagesFound') }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption">
            {{ $t('header.tryDifferentSearch') }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import { useTheme } from 'vuetify'
import { APP_LANGUAGE_DATA, APP_LANGUAGES } from '@/utils/constants'

// Timing constants
const DEBOUNCE_MS = 150
const MIN_SEARCH_CHARS = 2

export default {
  emits: ['changeLang'],
  data() {
    return {
      menuOpen: false,
      searchQuery: '',
      debouncedQuery: '',
      debounceTimer: null
    }
  },
  computed: {
    isThemeDark() {
      const theme = useTheme()
      return theme.global.name.value === 'dark'
    },
    // True when search has minimum characters
    isSearchActive() {
      return this.debouncedQuery.trim().length >= MIN_SEARCH_CHARS
    },
    // Hint shown when 1 character entered
    searchHint() {
      const len = this.searchQuery.trim().length
      if (len > 0 && len < MIN_SEARCH_CHARS) {
        return this.$t('header.typeMoreToSearch')
      }
      return null
    },
    // Filtered languages based on search query
    filteredLanguages() {
      return APP_LANGUAGES
        .map(locale => this.createLanguageItem(locale))
        .filter(item => item !== null)
        .sort((a, b) => a.native.localeCompare(b.native))
    },
    // Number of matches
    matchCount() {
      return this.filteredLanguages.length
    }
  },
  watch: {
    // Debounce search input
    searchQuery(newVal) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.debouncedQuery = newVal || ''
      }, DEBOUNCE_MS)
    }
  },
  beforeUnmount() {
    this.clearAllTimers()
  },
  methods: {
    // Create language item with match info for highlighting
    createLanguageItem(locale) {
      const lang = APP_LANGUAGE_DATA[locale]
      const query = this.debouncedQuery.toLowerCase().trim()

      // No query or too short: show all languages
      if (!query || query.length < MIN_SEARCH_CHARS) {
        return {
          locale,
          native: lang.native,
          english: lang.english,
          nativeMatch: null,
          englishMatch: null
        }
      }

      // Check for matches
      const exactLocaleMatch = locale.toLowerCase() === query
      const nativeMatch = this.findStartsWithMatch(lang.native, query)
      const englishMatch = this.findStartsWithMatch(lang.english, query)

      // No match found
      if (!exactLocaleMatch && !nativeMatch && !englishMatch) {
        return null
      }

      return {
        locale,
        native: lang.native,
        english: lang.english,
        nativeMatch: exactLocaleMatch ? null : nativeMatch,
        englishMatch: exactLocaleMatch ? null : englishMatch
      }
    },
    // Find starts-with match and return match length
    findStartsWithMatch(text, query) {
      const normalized = text.toLowerCase().normalize('NFC')
      const normalizedQuery = query.normalize('NFC')
      if (normalized.startsWith(normalizedQuery)) {
        return query.length
      }
      return null
    },
    // Highlight matched text portion
    highlightMatch(text, matchLength) {
      if (!matchLength) return this.escapeHtml(text)
      const matched = text.substring(0, matchLength)
      const rest = text.substring(matchLength)
      return `<mark>${this.escapeHtml(matched)}</mark>${this.escapeHtml(rest)}`
    },
    // Escape HTML to prevent XSS
    escapeHtml(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },
    // Select a language
    selectLanguage(locale) {
      this.closeMenu()
      this.$emit('changeLang', locale)
    },
    // Close menu and reset state
    closeMenu() {
      this.menuOpen = false
      this.resetState()
    },
    // Reset all state and timers
    resetState() {
      this.searchQuery = ''
      this.debouncedQuery = ''
      this.clearAllTimers()
    },
    // Handle menu toggle
    onMenuToggle(isOpen) {
      if (!isOpen) {
        this.resetState()
      }
    },
    // Handle Enter key - switch to single match if available
    onEnterKey() {
      if (this.filteredLanguages.length === 1) {
        this.selectLanguage(this.filteredLanguages[0].locale)
      }
    },
    // Handle Escape key - cancel and clear
    onEscapeKey() {
      this.resetState()
    },
    // Clear all timers (for cleanup)
    clearAllTimers() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card {
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 5px;
}
</style>
